import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import RestaurantMenuOutlined from '@mui/icons-material/RestaurantMenuOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import { DetailPageTemplate } from '../../../components/page-templates/detail-page/DetailPageTemplate';
import { InsightTile } from '../../../components/shared/tiles/InsightTile';
import BackButton from '../../../components/shared/actions/BackButton';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { UserContext } from '../../../components/shared/useUser';
import {
  rangeClient,
  MenuType,
  MenuStatus,
  MenuDisplayStatus,
  MenuPublishMode,
  RangeDto,
  RecipeCandidateDto,
} from '../../../app/services/api/range';
import useLogError from '../../../hooks/useLogError';
import { MenuDisplayStatusChip } from '../components/MenuDisplayStatusChip';
import MenuServicePeriodsTab from './components/MenuServicePeriodsTab';
import MenuRecipesTab from './components/MenuRecipesTab';
import MenuRangesTab from './components/MenuRangesTab';
import { ServicePeriodFormValue } from './components/ServicePeriodDialog';

const MENU_TYPES = Object.values(MenuType);

// "2026-10-01T00:00:00Z" <-> "2026-10-01" for native date inputs.
const toDateInputValue = (value?: string | null): string => (value ? value.slice(0, 10) : '');
const toIsoDate = (value: string): string | null => (value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null);
const toIsoEndOfDay = (value: string): string | null => (value ? new Date(`${value}T23:59:59.999Z`).toISOString() : null);

// "09:00:00" (backend TimeSpan) <-> "09:00" (native time input).
const toTimeInputValue = (value: string): string => value.slice(0, 5);
const toApiTime = (value: string): string => (value.length === 5 ? `${value}:00` : value);

const derivePublishMode = (status: MenuStatus, startDate?: string | null): MenuPublishMode => {
  if (status === MenuStatus.Draft) return 'draft';
  return startDate ? 'schedule' : 'publishNow';
};

const MenuForm = () => {
  const { t } = useTranslation('range');
  const navigate = useNavigate();
  const { menuId } = useParams<{ menuId: string }>();
  const { franchises, selectedFranchise, hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();

  const isEditMode = Boolean(menuId);
  const canViewRanges = hasPermissionTo(['RangeRead']);
  const canWrite = hasPermissionTo(['MenuWrite']);
  const canViewRecipes = hasPermissionTo(['RecipeRead']);
  const accessibleFranchises = useMemo(
    () => franchises.filter((franchise) => franchise.active !== false && franchise.franchiseId !== undefined),
    [franchises],
  );
  const activeFranchiseId = accessibleFranchises.some(
    (franchise) => franchise.franchiseId === selectedFranchise?.franchiseId,
  ) ? selectedFranchise?.franchiseId : undefined;
  const defaultFranchiseId = activeFranchiseId ?? (
    accessibleFranchises.length === 1 ? accessibleFranchises[0].franchiseId : undefined
  );

  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTabId, setActiveTabId] = useState('overview');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [menuType, setMenuType] = useState<MenuType | ''>('');
  const [nameError, setNameError] = useState('');
  const [franchiseId, setFranchiseId] = useState<number | undefined>(defaultFranchiseId);

  const [publishMode, setPublishMode] = useState<MenuPublishMode>('draft');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [loadedDisplayStatus, setLoadedDisplayStatus] = useState<MenuDisplayStatus | null>(null);


  const [servicePeriods, setServicePeriods] = useState<ServicePeriodFormValue[]>([]);
  const [assignedRecipeIds, setAssignedRecipeIds] = useState<Set<number>>(new Set());
  const [assignedRangeIds, setAssignedRangeIds] = useState<Set<number>>(new Set());
  const [originalAssignedRecipeIds, setOriginalAssignedRecipeIds] = useState<Set<number>>(new Set());
  const [originalAssignedRangeIds, setOriginalAssignedRangeIds] = useState<Set<number>>(new Set());

  const [ranges, setRanges] = useState<RangeDto[]>([]);
  const [isRangesLoading, setIsRangesLoading] = useState(true);
  const [recipes, setRecipes] = useState<RecipeCandidateDto[]>([]);
  const [isRecipesLoading, setIsRecipesLoading] = useState(true);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const franchiseForDisplay = useMemo(
    () => accessibleFranchises.find((franchise) => franchise.franchiseId === franchiseId) ?? selectedFranchise,
    [accessibleFranchises, franchiseId, selectedFranchise],
  );

  const handleFranchiseChange = useCallback((value: string) => {
    const nextFranchiseId = value ? Number(value) : undefined;
    if (nextFranchiseId === franchiseId) return;

    setFranchiseId(nextFranchiseId);
    setAssignedRangeIds(new Set());
    setOriginalAssignedRangeIds(new Set());
    setAssignedRecipeIds(new Set());
    setOriginalAssignedRecipeIds(new Set());
  }, [franchiseId]);

  useEffect(() => {
    if (!isEditMode) {
      setFranchiseId(defaultFranchiseId);
    }
  }, [defaultFranchiseId, isEditMode]);

  const loadMenu = useCallback(async () => {
    if (!menuId) return;
    setIsLoading(true);
    try {
      const numericId = Number(menuId);
      const menu = await rangeClient.getMenu(numericId);

      if (!menu) {
        setErrorMessage(t('loadErrorMessage'));
        return;
      }

      const [loadedServicePeriods, loadedRecipes] = await Promise.all([
        rangeClient.getServicePeriods(numericId),
        canViewRecipes ? rangeClient.getMenuRecipes(numericId) : Promise.resolve([]),
      ]);
      const loadedRanges = canViewRanges
        ? await rangeClient.getMenuRanges(numericId)
        : [];

      setName(menu.name);
      setDescription(menu.description ?? '');
      setMenuType(menu.menuType ?? '');
      setFranchiseId(menu.franchiseId);
      setLoadedDisplayStatus(menu.displayStatus);
      setPublishMode(derivePublishMode(menu.status, menu.startDate));
      setStartDate(toDateInputValue(menu.startDate));
      setEndDate(toDateInputValue(menu.endDate));
      const mappedServicePeriods = loadedServicePeriods.map((sp) => ({
        localId: `sp-${sp.id}`,
        name: sp.name,
        startTime: toTimeInputValue(sp.startTime),
        endTime: toTimeInputValue(sp.endTime),
        daysOfWeek: sp.daysOfWeek,
      }));
      setServicePeriods(mappedServicePeriods);
      const recipeIds = new Set(loadedRecipes.map((recipe) => recipe.recipeId));
      const rangeIds = new Set(loadedRanges.map((range) => range.rangeId));
      setAssignedRecipeIds(recipeIds);
      setOriginalAssignedRecipeIds(recipeIds);
      setAssignedRangeIds(rangeIds);
      setOriginalAssignedRangeIds(rangeIds);
    } catch (error) {
      logError(error);
      setErrorMessage(t('loadErrorMessage'));
    } finally {
      setIsLoading(false);
    }
    // logError's identity changes every render (useLogError isn't memoized) - omit it to avoid an infinite fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId, t, canViewRanges, canViewRecipes]);

  useEffect(() => {
    loadMenu();
  }, [loadMenu]);

  useEffect(() => {
    if (!franchiseId) {
      setRanges([]);
      setIsRangesLoading(false);
      setRecipes([]);
      setIsRecipesLoading(false);
      return;
    }

    let active = true;
    if (canViewRanges) {
      setIsRangesLoading(true);
      rangeClient.getRanges({ page: 1, pageSize: 2147483647 })
        .then((result) => {
          if (active) setRanges(result.data.filter((range) => range.franchiseId === franchiseId));
        })
        .catch(() => { if (active) setRanges([]); })
        .finally(() => { if (active) setIsRangesLoading(false); });
    } else {
      setRanges([]);
      setIsRangesLoading(false);
    }

    if (canViewRecipes) {
      setIsRecipesLoading(true);
      rangeClient.getAvailableRecipes({ franchiseId, page: 1, pageSize: 2147483647 })
        .then((result) => { if (active) setRecipes(result.data); })
        .catch(() => { if (active) setRecipes([]); })
        .finally(() => { if (active) setIsRecipesLoading(false); });
    } else {
      setRecipes([]);
      setIsRecipesLoading(false);
    }

    return () => {
      active = false;
    };
  }, [franchiseId, canViewRanges, canViewRecipes]);

  const handleToggleRecipe = (recipeId: number) => {
    setAssignedRecipeIds((prev) => {
      const next = new Set(prev);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  };

  const handleToggleRange = (rangeId: number) => {
    setAssignedRangeIds((prev) => {
      const next = new Set(prev);
      if (next.has(rangeId)) next.delete(rangeId);
      else next.add(rangeId);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError(t('nameRequired'));
      setActiveTabId('overview');
      return;
    }
    if (!franchiseId) {
      setErrorMessage(t('franchiseRequired'));
      return;
    }
    if (servicePeriods.some((servicePeriod) => !servicePeriod.name.trim())) {
      setErrorMessage(t('servicePeriodNameRequired'));
      setActiveTabId('servicePeriods');
      return;
    }
    if (publishMode === 'schedule' && !startDate) {
      setStartDateError(t('startDateRequired'));
      setActiveTabId('overview');
      return;
    }
    if (publishMode !== 'publishNow' && startDate && endDate && endDate < startDate) {
      setEndDateError(t('endDateBeforeStartDate'));
      setActiveTabId('overview');
      return;
    }

    let status: MenuStatus;
    let finalStartDate: string | null;
    let finalEndDate: string | null;

    if (publishMode === 'draft') {
      status = MenuStatus.Draft;
      finalStartDate = toIsoDate(startDate);
      finalEndDate = toIsoEndOfDay(endDate);
    } else if (publishMode === 'publishNow') {
      status = MenuStatus.Published;
      finalStartDate = null;
      finalEndDate = null;
    } else {
      status = MenuStatus.Published;
      finalStartDate = toIsoDate(startDate);
      finalEndDate = toIsoEndOfDay(endDate);
    }

    setIsSaving(true);
    try {
      const assignmentPayload = {
        rangeIds: Array.from(assignedRangeIds),
        recipeIds: canViewRecipes ? Array.from(assignedRecipeIds) : [],
        servicePeriods: servicePeriods.map((sp) => ({
          name: sp.name,
          startTime: toApiTime(sp.startTime),
          endTime: toApiTime(sp.endTime),
          daysOfWeek: sp.daysOfWeek,
        })),
      };

      const menuPayload = {
        franchiseId,
        name: name.trim(),
        description: description.trim() || null,
        menuType: menuType || null,
        status,
        startDate: finalStartDate,
        endDate: finalEndDate,
        ...assignmentPayload,
      };

      if (isEditMode && menuId) {
        await rangeClient.updateMenu(Number(menuId), menuPayload);
        await loadMenu();
        setSuccessMessage(t('menuSavedSuccessMessage'));
      } else {
        await rangeClient.createMenu(menuPayload);
        navigate('/range/menus', {
          state: { successMessage: t('menuSavedSuccessMessage') },
        });
      }
    } catch (error) {
      logError(error);
      setErrorMessage(extractErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const previewDisplayStatus = useMemo<MenuDisplayStatus>(() => {
    if (isEditMode && loadedDisplayStatus) return loadedDisplayStatus;
    if (publishMode === 'draft') return MenuDisplayStatus.Draft;
    if (publishMode === 'schedule' && startDate && new Date(startDate) > new Date()) return MenuDisplayStatus.Scheduled;
    return MenuDisplayStatus.Active;
  }, [isEditMode, loadedDisplayStatus, publishMode, startDate]);

  const tabs = useMemo(() => {
    const items = [
      {
        id: 'overview',
        label: t('overview'),
        content: (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 560 }}>
            {!isEditMode && accessibleFranchises.length > 1 && (
              <TextField
                select
                label={t('franchise')}
                value={franchiseId ?? ''}
                onChange={(e) => handleFranchiseChange(e.target.value)}
                required
                fullWidth
                disabled={!canWrite || accessibleFranchises.length <= 1}
              >
                <MenuItem value="">
                  <em>{t('selectFranchise')}</em>
                </MenuItem>
                {accessibleFranchises.map((franchise) => (
                  <MenuItem key={franchise.franchiseId}
                    value={franchise.franchiseId}>
                    {franchise.franchiseName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label={t('name')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              error={Boolean(nameError)}
              helperText={nameError}
              required
              fullWidth
              disabled={!canWrite}
            />
            <TextField
              label={t('description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              disabled={!canWrite}
            />
            <TextField
              select
              label={t('menuType')}
              value={menuType}
              onChange={(e) => setMenuType(e.target.value as MenuType | '')}
              fullWidth
              disabled={!canWrite}
              InputLabelProps={{ shrink: true }}
            >
              {MENU_TYPES.map((type) => (
                <MenuItem
                  key={type}
                  value={type}
                >
                  {t(`menuType${type}`)}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography
                variant="textSM"
                fontWeight={600}
                sx={{ mb: 1 }}
              >
                {t('publishing')}
              </Typography>
              <RadioGroup
                value={publishMode}
                onChange={(e) => setPublishMode(e.target.value as MenuPublishMode)}
              >
                <FormControlLabel
                  value="draft"
                  control={<Radio disabled={!canWrite} />}
                  label={t('publishModeDraft')}
                />
                <FormControlLabel
                  value="publishNow"
                  control={<Radio disabled={!canWrite} />}
                  label={t('publishModePublishNow')}
                />
                <FormControlLabel
                  value="schedule"
                  control={<Radio disabled={!canWrite} />}
                  label={t('publishModeSchedule')}
                />
              </RadioGroup>
              {publishMode === 'schedule' && (
                <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                  <TextField
                    label={t('startDate')}
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const nextStartDate = e.target.value;
                      setStartDate(nextStartDate);
                      if (endDate && nextStartDate && endDate < nextStartDate) {
                        setEndDate('');
                      }
                      if (startDateError) setStartDateError('');
                      if (endDateError) setEndDateError('');
                    }}
                    error={Boolean(startDateError)}
                    helperText={startDateError}
                    InputLabelProps={{ shrink: true }}
                    disabled={!canWrite}
                    fullWidth
                  />
                  <TextField
                    label={t('endDate')}
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      if (endDateError) setEndDateError('');
                    }}
                    error={Boolean(endDateError)}
                    helperText={endDateError}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: startDate || undefined }}
                    disabled={!canWrite}
                    fullWidth
                  />
                </Box>
              )}
            </Box>
          </Box>
        ),
      },
      {
        id: 'servicePeriods',
        label: t('servicePeriods'),
        content: (
          <MenuServicePeriodsTab
            servicePeriods={servicePeriods}
            onChange={setServicePeriods}
            readOnly={!canWrite}
          />
        ),
      },
    ];

    if (canViewRecipes) {
      items.push({
        id: 'recipes',
        label: t('recipes'),
        content: franchiseId ? (
          <MenuRecipesTab
            recipes={recipes}
            isLoading={isRecipesLoading}
            assignedRecipeIds={assignedRecipeIds}
            originalAssignedRecipeIds={originalAssignedRecipeIds}
            onToggle={handleToggleRecipe}
            readOnly={!canWrite}
          />
        ) : (
          <Typography color="text.secondary">{t('selectFranchise')}</Typography>
        ),
      });
    }

    if (canViewRanges) {
      items.push({
        id: 'ranges',
        label: t('ranges'),
        content: franchiseId ? (
          <MenuRangesTab
            ranges={ranges}
            isLoading={isRangesLoading}
            assignedRangeIds={assignedRangeIds}
            originalAssignedRangeIds={originalAssignedRangeIds}
            onToggle={handleToggleRange}
            onRangeClick={(rangeId) => navigate(`/range/ranges/${rangeId}`)}
            readOnly={!canWrite}
          />
        ) : (
          <Typography color="text.secondary">{t('selectFranchise')}</Typography>
        ),
      });
    }

    return items;
  }, [
    t,
    name,
    nameError,
    description,
    menuType,
    canWrite,
    canViewRecipes,
    canViewRanges,
    accessibleFranchises,
    franchiseForDisplay,
    handleFranchiseChange,
    publishMode,
    startDate,
    startDateError,
    endDate,
    servicePeriods,
    franchiseId,
    recipes,
    isRecipesLoading,
    assignedRecipeIds,
    originalAssignedRecipeIds,
    ranges,
    isRangesLoading,
    assignedRangeIds,
    originalAssignedRangeIds,
    navigate,
  ]);

  useEffect(() => {
    if (tabs.some((tab) => tab.id === activeTabId)) return;
    setActiveTabId(tabs[0]?.id ?? 'overview');
  }, [tabs, activeTabId]);

  return (
    <DetailPageTemplate
      title={isEditMode ? t('editMenu') : t('createMenu')}
      loading={isLoading}
      backButton={(
        <BackButton
          title={t('backToMenus')}
          isLink={false}
          handleClick={() => navigate('/range/menus')}
        />
      )}
      headlineContent={{
        layout: 'six',
        insights: [
          <InsightTile
            key="franchise"
            label={t('franchise')}
            value={franchiseForDisplay?.franchiseName ?? '-'}
            description={t('menuFranchiseTileDescription')}
            icon={<BusinessOutlined />}
            variant="secondary"
          />,
          <InsightTile
            key="status"
            label={t('status')}
            value={<MenuDisplayStatusChip status={previewDisplayStatus} />}
            description={t('menuStatusTileDescription')}
            icon={<ScheduleOutlined />}
            variant="primary"
          />,
          <InsightTile
            key="menuType"
            label={t('menuType')}
            value={menuType ? t(`menuType${menuType}`) : ''}
            description={t('menuTypeTileDescription')}
            icon={<MenuBookOutlined />}
            variant="default"
          />,
          <InsightTile
            key="recipes"
            label={t('recipes')}
            value={assignedRecipeIds.size}
            description={t('menuRecipesTileDescription')}
            icon={<RestaurantMenuOutlined />}
            variant="success"
            loading={isRecipesLoading}
          />,
          <InsightTile
            key="ranges"
            label={t('ranges')}
            value={assignedRangeIds.size}
            description={t('menuRangesTileDescription')}
            icon={<MenuBookOutlined />}
            variant="secondary"
            loading={isRangesLoading}
          />,
          <InsightTile
            key="servicePeriods"
            label={t('servicePeriods')}
            value={servicePeriods.length}
            description={t('menuServicePeriodsTileDescription')}
            icon={<ScheduleOutlined />}
            variant="default"
          />,
        ],
      }}
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      actions={(
        <>
          <Button
            variant="secondary"
            onClick={() => navigate('/range/menus')}
            disabled={isSaving}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || !canWrite}
          >
            {t('save')}
          </Button>
        </>
      )}
    >
      <MessagesSnackbar
        open={Boolean(successMessage)}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        severity="success"
        duration={4000}
      />
      <MessagesSnackbar
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
        severity="error"
        duration={5000}
      />
    </DetailPageTemplate>
  );
};

export default MenuForm;
